import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import logger from "@/utils/logger";
import {
  PiDownloadSimple,
  PiReceipt,
  PiChartLine,
  PiStar,
} from "react-icons/pi";

const CHOOSE_ICONS: Record<string, React.ElementType> = {
  download: PiDownloadSimple,
  receipt: PiReceipt,
  chart: PiChartLine,
  star: PiStar,
};

const ICON_KEYS = Object.keys(CHOOSE_ICONS);

interface ChooseItem {
  title: string;
  description: string;
  image?: string;
  icon?: string;
}

const cardBgs = [
  "bg-[#E8FFF3]",
  "bg-[#F0F5FF]",
  "bg-[#FDF0FF]",
  "bg-[#FFF0E8]",
];

const defaultIconSrcs = [
  "/assets/img/icon/choose_1_1.svg",
  "/assets/img/icon/choose_1_2.svg",
  "/assets/img/icon/choose_1_3.svg",
  "/assets/img/icon/choose_1_4.svg",
];

const getIconSrc = (item: ChooseItem, idx: number) => {
  if (item.image) return item.image;
  if (item.icon === "download") return "/assets/img/icon/choose_1_1.svg";
  if (item.icon === "receipt") return "/assets/img/icon/choose_1_2.svg";
  if (item.icon === "chart") return "/assets/img/icon/choose_1_3.svg";
  if (item.icon === "star") return "/assets/img/icon/choose_1_4.svg";
  return defaultIconSrcs[idx % defaultIconSrcs.length];
};

export default function HokChooseUs({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const defaultItems: ChooseItem[] = [
    {
      title: "Instant Downloads & License Delivery",
      description: "Hok is affordable for all WordPress developers. Have the freedom of choosing how you want to sell (Easy Digital Downloads, WooCommerce, FastSpring or Paddle) while saving time & money.",
      image: "/assets/img/icon/choose_1_1.svg",
    },
    {
      title: "Tax & Invoicing Automation",
      description: "Hok is affordable for all WordPress developers. Have the freedom of choosing how you want to sell (Easy Digital Downloads, WooCommerce, FastSpring or Paddle) while saving time & money.",
      image: "/assets/img/icon/choose_1_2.svg",
    },
    {
      title: "Analytics & Sales Reporting",
      description: "Hok is affordable for all WordPress developers. Have the freedom of choosing how you want to sell (Easy Digital Downloads, WooCommerce, FastSpring or Paddle) while saving time & money.",
      image: "/assets/img/icon/choose_1_3.svg",
    },
    {
      title: "Top Reviews & Moderation",
      description: "Hok is affordable for all WordPress developers. Have the freedom of choosing how you want to sell (Easy Digital Downloads, WooCommerce, FastSpring or Paddle) while saving time & money.",
      image: "/assets/img/icon/choose_1_4.svg",
    },
  ];

  const items: ChooseItem[] = content.items || defaultItems;

  const updateItem = (idx: number, field: keyof ChooseItem, value: string) => {
    if (!onContentChange) return;
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    onContentChange({ ...content, items: newItems });
  };

  const addItem = () => {
    if (!onContentChange) return;
    const newItems = [...items, { 
      title: "New Feature", 
      description: "Feature description.", 
      image: defaultIconSrcs[items.length % defaultIconSrcs.length] 
    }];
    onContentChange({ ...content, items: newItems });
  };

  const removeItem = (idx: number) => {
    if (!onContentChange) return;
    const newItems = items.filter((_, i) => i !== idx);
    onContentChange({ ...content, items: newItems });
  };

  return (
    <div
      className={`relative overflow-hidden w-full py-10 sm:py-14 bg-white ${isSelected ? "ring-2 ring-blue-500 relative z-20" : ""}`}
      style={{
        fontFamily,
      }}
    >
      {/* Solid Decorative Shapes from Mockup */}
      {/* Top-Left Purple Capsule */}
      <div className="absolute top-12 left-12 w-14 h-7 rounded-full bg-[#684DF4]/20 rotate-12 pointer-events-none z-0" />
      
      {/* Left-Center Purple Organic Shape */}
      <div className="absolute top-[18%] -left-16 w-52 h-44 bg-gradient-to-br from-[#EEF2FF] to-[#E3DCFF] rounded-r-[150px] rounded-l-[50px] rotate-[12deg] pointer-events-none z-0" />
      
      {/* Top-Right Light Green Polygon Shape */}
      <div className="absolute top-24 -right-12 w-48 h-36 bg-[#E8FFF0] rounded-l-[140px] rounded-r-[80px] rotate-[-12deg] pointer-events-none z-0" />
      
      {/* Bottom-Left Soft Pink Semi-Circle */}
      <div className="absolute -bottom-8 left-16 w-32 h-32 rounded-full bg-[#FFF0F5] pointer-events-none z-0" />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
        {/* Section header */}
        <div className="flex justify-center mb-10 sm:mb-14">
          <div className="text-center max-w-2xl">
            <div className="inline-block border border-[#684DF4]/20 text-[#684DF4] bg-[#684DF4]/5 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              <EditableText
                value={content.subtitle || "Why Choose Us"}
                onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                as="span"
                placeholder="Subtitle"
              />
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4 tracking-tight">
              {(!content.title || content.title === "Why Choose Instacks?" || content.title === "Why Choose Us?") ? (
                <>
                  <span style={{ color: primaryColor }}>Why Choose</span> Us?
                </>
              ) : (
                <span style={{ color: primaryColor }}>{content.title}</span>
              )}
              <div className="hidden">
                <EditableText
                  value={content.title || "Why Choose Us?"}
                  onChange={(val) => onContentChange?.({ ...content, title: val })}
                  as="span"
                  placeholder="Title"
                />
              </div>
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              <EditableText
                value={content.description || "Explore over 8,000+ website templates and themes."}
                onChange={(val) => onContentChange?.({ ...content, description: val })}
                as="span"
                placeholder="Description"
              />
            </p>
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {items.map((item, idx) => {
            const cardBgClass = cardBgs[idx % cardBgs.length];
            return (
              <div
                key={idx}
                className={`group relative flex flex-col items-center text-center p-6 sm:p-8 rounded-3xl ${cardBgClass} transition-all duration-300 cursor-pointer hover:shadow-md hover:-translate-y-1 min-h-[360px]`}
              >
                {isSelected && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeItem(idx);
                    }}
                    className="absolute top-3 right-3 h-7 w-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm font-bold z-20"
                    title="Remove"
                  >
                    ×
                  </button>
                )}

                {/* Icon circle */}
                <div className="relative w-20 h-20 rounded-full bg-white shadow-[0_8px_30px_rgb(0,0,0,0.02)] flex items-center justify-center mb-6 transition-transform group-hover:scale-105">
                  <img
                    src={getIconSrc(item, idx)}
                    alt={item.title}
                    className="w-10 h-10 object-contain pointer-events-none"
                  />
                  {isSelected && (
                    <label className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 rounded-full hover:bg-black/80 cursor-pointer transition-all opacity-0 group-hover:opacity-100 font-bold text-xs text-white">
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onClick={(e) => e.stopPropagation()}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const toast = (await import("react-hot-toast")).default;
                            const homepageApi = (await import("@/services/homepageApi")).default;
                            const toastId = toast.loading("Uploading icon...");
                            try {
                              const response = await homepageApi.uploadImage(file);
                              const url = response.data?.url || response.url;
                              if (url) {
                                updateItem(idx, "image", url);
                                toast.success("Icon updated!", { id: toastId });
                              } else {
                                toast.error("Failed to upload.", { id: toastId });
                              }
                            } catch (err) {
                              toast.error("Upload failed", { id: toastId });
                              logger.error(err);
                            }
                          }
                        }}
                      />
                    </label>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-gray-900 font-bold text-lg mb-3 tracking-tight leading-snug">
                  <EditableText
                    value={item.title}
                    onChange={(val) => updateItem(idx, "title", val)}
                    as="span"
                    placeholder="Feature Title"
                  />
                </h3>

                {/* Description */}
                <p className="text-gray-500 text-sm leading-relaxed">
                  <EditableText
                    value={item.description}
                    onChange={(val) => updateItem(idx, "description", val)}
                    as="span"
                    placeholder="Feature description..."
                  />
                </p>
              </div>
            );
          })}

          {isSelected && (
            <button
              onClick={addItem}
              className="flex flex-col items-center justify-center min-h-[360px] rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-600 transition-all"
            >
              <span className="text-4xl mb-2 opacity-50">+</span>
              <span className="text-sm font-semibold opacity-70">Add Feature</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
